import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NCRAction, NCRActionSearchParams } from '../../services/ncractionApi';

// State interfaces
export interface NCRActionState {
  // Data
  items: NCRAction[];
  selectedItem: NCRAction | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    Description: string;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: NCRActionSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: NCRActionState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    Description: '',
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const ncractionSlice = createSlice({
  name: 'ncraction',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<NCRAction[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<NCRAction | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<NCRAction>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<NCRAction>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<NCRActionState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
      // Clear validation errors when form data changes
      if (state.validationErrors.length > 0) {
        state.validationErrors = [];
      }
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
      state.validationErrors = [];
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<NCRActionSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<NCRActionState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<NCRActionState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetNCRActionState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetNCRActionState,
} = ncractionSlice.actions;

// Saga action types
export const FETCH_NCRACTIONS = 'ncraction/fetchNCRActions';
export const FETCH_NCRACTION_BY_ID = 'ncraction/fetchNCRActionById';
export const CREATE_NCRACTION = 'ncraction/createNCRAction';
export const UPDATE_NCRACTION = 'ncraction/updateNCRAction';
export const DELETE_NCRACTION = 'ncraction/deleteNCRAction';
export const SEARCH_NCRACTIONS = 'ncraction/searchNCRActions';
export const FETCH_STATISTICS = 'ncraction/fetchStatistics';
export const BULK_ACTIVATE = 'ncraction/bulkActivate';
export const BULK_DEACTIVATE = 'ncraction/bulkDeactivate';
export const BULK_DELETE = 'ncraction/bulkDelete';
export const EXPORT_TO_CSV = 'ncraction/exportToCSV';

// Saga action creators
export const fetchNCRActions = (params?: NCRActionSearchParams) => ({ type: FETCH_NCRACTIONS, payload: params });
export const fetchNCRActionById = (id: number) => ({ type: FETCH_NCRACTION_BY_ID, payload: id });
export const createNCRAction = (ncraction: Omit<NCRAction, 'Id'>) => ({ type: CREATE_NCRACTION, payload: ncraction });
export const updateNCRAction = (id: number, ncraction: Partial<NCRAction>) => ({ type: UPDATE_NCRACTION, payload: { id, ncraction } });
export const deleteNCRAction = (id: number) => ({ type: DELETE_NCRACTION, payload: id });
export const searchNCRActions = (params: NCRActionSearchParams) => ({ type: SEARCH_NCRACTIONS, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: NCRActionSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default ncractionSlice.reducer; 